/**
 * ContainerContentsList Component for UI-Model-1
 *
 * Collapsible section listing all child items and nested containers
 * inside a selected container. Supports:
 * - Drag-and-drop reordering of items within a container
 * - Drag-and-drop moving items between parent/child containers
 * - Visibility toggle (eye icon)
 * - Delete with confirmation dialog
 * - Click to select item/container
 *
 * Follows API-Direct architecture:
 * - Reads from ContainerAPI.content.items / nestedContainers
 * - Dispatches via EXECUTE_COMMAND (auto-marks dirty)
 * - Uses useContainerOperations for removeItem / removeContainer / updateItem
 * - Uses useSelection for selectItem / selectContainer
 *
 * A single DragDropContext wraps the entire tree (parent + all nested
 * containers) so items can be dragged between any container in the
 * same root hierarchy — matching the canvas drag-and-drop behaviour.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { ContainerAPI, ItemAPI, isItemAPI } from '../../types/state.types';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { useSelection } from '../../contexts/SelectionContext';
import { useContainerOperations } from '../../hooks/useContainerOperations';
import { createReorderItemsCommand, createMoveItemCommand } from '../../types/command.types';
import * as selectors from '../../selectors';
import { useModule } from '../../contexts/ModuleContext';
import { canAddSubmodelItemToContainer } from '../../container-management/validators/submodelLockValidator';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { getItemIcon, getItemName, getItemTypeName, truncateName } from '../../utils/itemDisplayHelpers';
import { DropdownMenu } from '../Common/DropdownMenu';
import { ConfirmationDialog } from '../Common/ConfirmationDialog';
import {
  DragIcon,
  OverFlowIcon,
  ShowEyeIcon,
  HideEyeIcon,
  UpArrow,
  DownArrow,
  DeleteIcon,
} from 'assests/GmsIcons';
import { NestedContainerItem } from './NestedContainerItem';

interface ContainerContentsListProps {
  container: ContainerAPI;
}

interface DeleteConfirmation {
  isOpen: boolean;
  itemId: string;
  itemName: string;
  type: 'item' | 'container';
}

const INITIAL_DELETE_STATE: DeleteConfirmation = {
  isOpen: false,
  itemId: '',
  itemName: '',
  type: 'item',
};

export const ContainerContentsList = ({
  container,
}: ContainerContentsListProps) => {
  const { state, dispatch, getContainer } = useUIModelState();
  const { getSubmodelInstanceLimits } = useModule();
  const { selectItem, selectContainer } = useSelection();
  const { removeItem, updateItem, removeContainer } = useContainerOperations();

  const [isExpanded, setIsExpanded] = useState(true);
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmation>(INITIAL_DELETE_STATE);

  // Filter items to actual ItemAPI entries (exclude NestedContainerItemAPI)
  const items: ItemAPI[] = useMemo(() => {
    return (container.content?.items || []).filter(isItemAPI) as ItemAPI[];
  }, [container.content?.items]);

  const nestedContainers = container.content?.nestedContainers || [];
  const hasContents = items.length > 0 || nestedContainers.length > 0;

  // ── Drag & Drop ──────────────────────────────────────────────────────────

  /**
   * Resolve items for a given containerId (works for both parent and nested).
   */
  const getItemsForContainer = useCallback(
    (containerId: string): ItemAPI[] => {
      const { element } = getContainer(containerId);
      if (!element?.content?.items) return [];
      return element.content.items.filter((i): i is ItemAPI => isItemAPI(i));
    },
    [getContainer]
  );

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const { source, destination, draggableId } = result;
      if (!destination) return;

      const fromContainerId = source.droppableId;
      const toContainerId = destination.droppableId;

      // Same container — reorder
      if (fromContainerId === toContainerId) {
        if (source.index === destination.index) return;

        const { path } = getContainer(fromContainerId);
        const canvasId = path?.canvasId;
        if (!canvasId) return;

        const containerItems = getItemsForContainer(fromContainerId);
        const reordered = Array.from(containerItems);
        const [moved] = reordered.splice(source.index, 1);
        reordered.splice(destination.index, 0, moved);

        const command = createReorderItemsCommand({
          canvasId,
          containerId: fromContainerId,
          itemIds: reordered.map((item) => item.id),
        });
        dispatch({ type: 'EXECUTE_COMMAND', payload: command });
        return;
      }

      // Cross-container move
      const { path: fromPath } = getContainer(fromContainerId);
      const { path: toPath } = getContainer(toContainerId);
      const fromCanvasId = fromPath?.canvasId;
      const toCanvasId = toPath?.canvasId;
      if (!fromCanvasId || !toCanvasId) return;

      // Free style lifts the submodel movement restrictions below — items move anywhere.
      if (!selectors.isFreestyle(state.model)) {
        // Validate: must be within the same root container hierarchy
        const rootFrom = selectors.selectRootContainer(state, fromContainerId);
        const rootTo = selectors.selectRootContainer(state, toContainerId);
        if (!rootFrom || !rootTo || rootFrom.id !== rootTo.id) return;

        // Submodel lock: block moving an item into a container locked to a DIFFERENT submodel
        // (also enforces the max>1 cascade + max propagation). Empty/unlocked targets are allowed.
        const movedItem = getItemsForContainer(fromContainerId).find((i) => i.id === draggableId);
        if (movedItem?.parentId) {
          const toContainers = state.model?.layout.canvas[toCanvasId]?.containers || [];
          const lock = canAddSubmodelItemToContainer(
            toContainers,
            toContainerId,
            movedItem.parentId,
            (smId) => getSubmodelInstanceLimits(smId).maxInstantiation
          );
          if (!lock.allowed) {
            toast.error(lock.message || 'Cannot move this item here.');
            return;
          }
        }
      }

      const command = createMoveItemCommand(
        {
          itemId: draggableId,
          fromCanvasId,
          toCanvasId,
          fromContainerId,
          toContainerId,
          fromIndex: source.index,
          toIndex: destination.index,
        },
        'Move item to different container'
      );
      dispatch({ type: 'EXECUTE_COMMAND', payload: command });
    },
    [state, getContainer, getItemsForContainer, dispatch, getSubmodelInstanceLimits]
  );

  // ── Visibility Toggle ────────────────────────────────────────────────────

  const handleToggleVisibility = useCallback(
    (e: React.MouseEvent, item: ItemAPI) => {
      e.stopPropagation();
      const currentVisible = item.configuration?.isVisible ?? true;
      updateItem(item.id, {
        configuration: {
          ...item.configuration,
          isVisible: !currentVisible,
        },
      });
    },
    [updateItem]
  );

  // ── Delete Flow ──────────────────────────────────────────────────────────

  const openDeleteDialog = useCallback(
    (id: string, name: string, type: 'item' | 'container') => {
      setDeleteConfirmation({ isOpen: true, itemId: id, itemName: name, type });
    },
    []
  );

  const handleConfirmDelete = useCallback(() => {
    if (deleteConfirmation.type === 'item') {
      removeItem(deleteConfirmation.itemId);
    } else {
      removeContainer(deleteConfirmation.itemId);
    }
    setDeleteConfirmation(INITIAL_DELETE_STATE);
  }, [deleteConfirmation, removeItem, removeContainer]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmation(INITIAL_DELETE_STATE);
  }, []);

  // ── Item Click (select) ──────────────────────────────────────────────────

  const handleItemClick = useCallback(
    (item: ItemAPI) => {
      selectItem(item.id, item.type, container.id);
    },
    [selectItem, container.id]
  );

  // ── Nested Container Click ───────────────────────────────────────────────

  const handleNestedContainerClick = useCallback(
    (nestedId: string) => {
      selectContainer(nestedId);
    },
    [selectContainer]
  );

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="tw-border-t tw-border-edge">
      {/* Collapsible Header */}
      <div role="button" data-testid="configpanel-containercontentslist-div-1"
        className="tw-flex tw-justify-between tw-items-center tw-cursor-pointer tw-p-3 tw-bg-surface-sunken hover:tw-bg-surface-hover tw-rounded"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3 className="tw-text-ink-secondary tw-font-semibold tw-text-sm">
          Added Components or Container
        </h3>
        <button data-testid="configpanel-containercontentslist-button-1"
          className="tw-p-1 tw-rounded hover:tw-bg-surface-hover tw-text-ink-secondary icon-follow-ink"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? <UpArrow /> : <DownArrow />}
        </button>
      </div>

      {/* Expanded Content — single DragDropContext for the entire tree */}
      {isExpanded && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="tw-mt-1">
            {/* Items List (always render Droppable so empty containers accept drops) */}
              <Droppable droppableId={container.id} type="config-panel-item">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`tw-space-y-1 tw-ps-4 ${
                      snapshot.isDraggingOver ? 'tw-bg-accent-tint tw-rounded' : ''
                    }`}
                  >
                    {items.map((item, index) => {
                      const name = getItemName(item);
                      const typeName = getItemTypeName(item);
                      const isVisible = item.configuration?.isVisible ?? true;

                      return (
                        <Draggable key={item.id} draggableId={item.id} index={index}>
                          {(dragProvided, dragSnapshot) => (
                            <div role="button" data-testid={`configpanel-containercontentslist-div-2-${index}`}
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              className={`tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1 tw-rounded tw-cursor-pointer hover:tw-bg-surface-hover tw-group ${
                                dragSnapshot.isDragging
                                  ? 'tw-bg-accent-tint tw-shadow-lg'
                                  : ''
                              }`}
                              onClick={() => handleItemClick(item)}
                            >
                              {/* Drag Handle */}
                              <div
                                {...dragProvided.dragHandleProps}
                                className="tw-cursor-move tw-text-ink-tertiary icon-follow-ink"
                              >
                                <DragIcon />
                              </div>

                              {/* Type Icon */}
                              <div className="tw-flex-shrink-0 tw-w-4 tw-flex tw-items-center tw-justify-center tw-text-ink-secondary">
                                {getItemIcon(item)}
                              </div>

                              {/* Eye Toggle */}
                              <button data-testid={`configpanel-containercontentslist-button-2-${index}`}
                                className="tw-flex-shrink-0 tw-p-1 tw-rounded hover:tw-bg-surface-hover tw-transition-colors tw-text-ink-secondary icon-follow-ink"
                                onClick={(e) => handleToggleVisibility(e, item)}
                                title={isVisible ? 'Hide item' : 'Show item'}
                              >
                                {isVisible ? <ShowEyeIcon /> : <HideEyeIcon />}
                              </button>

                              {/* Name + Type */}
                              <div className="tw-flex-1 tw-min-w-0">
                                <span
                                  className="tw-text-sm tw-text-ink tw-block tw-truncate"
                                  title={`${name} (${typeName})`}
                                >
                                  {truncateName(name)}
                                  <span className="tw-text-xs tw-text-ink-tertiary tw-ml-1">
                                    ({typeName})
                                  </span>
                                </span>
                              </div>

                              {/* Three-dot Menu */}
                              <div>
                                <DropdownMenu
                                  trigger={<OverFlowIcon />}
                                  position="right"
                                  items={[
                                    {
                                      id: 'delete',
                                      label: 'Delete',
                                      icon: <DeleteIcon />,
                                      onClick: () =>
                                        openDeleteDialog(item.id, name, 'item'),
                                      className:
                                        'tw-text-tone-danger-ink hover:tw-bg-tone-danger-tint',
                                    },
                                  ]}
                                />
                              </div>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                    {items.length === 0 && (
                      <div className={`tw-py-3 tw-text-center tw-text-xs tw-rounded tw-border tw-border-dashed ${
                        snapshot.isDraggingOver
                          ? 'tw-bg-accent-tint tw-border-accent tw-text-accent'
                          : 'tw-border-edge tw-text-ink-tertiary'
                      }`}>
                        Drop items here
                      </div>
                    )}
                  </div>
                )}
              </Droppable>

            {/* Nested Containers (expandable tree) */}
            {nestedContainers.length > 0 && (
              <div className="tw-mt-2">
                <div className="tw-text-xs tw-font-semibold tw-text-ink-secondary tw-mb-2 tw-ps-3">
                  Nested Containers
                </div>
                <div className="tw-space-y-1">
                  {nestedContainers.map((nested) => (
                    <NestedContainerItem
                      key={nested.id}
                      nestedContainer={nested}
                      level={1}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {!hasContents && (
              <div className="tw-text-sm tw-text-ink-tertiary tw-text-center tw-py-4">
                No contents in this container
              </div>
            )}
          </div>
        </DragDropContext>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteConfirmation.isOpen}
        title={deleteConfirmation.type === 'item' ? 'Delete Item' : 'Delete Container'}
        message={`Are you sure you want to delete "${deleteConfirmation.itemName}"?`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default ContainerContentsList;
